import { useEffect, useReducer, useRef } from 'react';

import { TFetchError } from '@/api/api-error.types';
import { _mergeBusinessCard } from '@/api/contact-import/mutations';
import MergeIcon from '@/assets/svg/ui-elements/merge.svg';
import { useToast } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { addCategoriesToContact, createContact } from './api';
import { reducer } from './reducer';
import { buildCardMatchPayload } from './steps/review-card-drawer/use-card-match';
import { createInitialState } from './types';

export function useAddContactFlow(
  initialCode?: string,
  presetCategoryIds?: string[],
  startWithManualEntry = false,
) {
  const [state, dispatch] = useReducer(reducer, undefined, () => {
    const start = createInitialState(presetCategoryIds, startWithManualEntry);
    return initialCode
      ? { ...start, step: 'qrScan' as const, method: 'qr' as const, pendingCode: initialCode }
      : start;
  });
  const inflight = useRef<'creating' | 'merging' | null>(null);
  const { add: addToast } = useToast();

  useEffect(() => {
    if (state.step !== 'creating') return;
    if (inflight.current === 'creating') return;
    inflight.current = 'creating';
    let cancelled = false;
    const isCard = state.method === 'card';
    const contactName = state.data.name?.trim();
    createContact({
      ...state.data,
      categoryIds: state.categoryIds,
      qrReferenceId: state.qrReferenceId,
      qrContactCode: state.qrContactCode,
    })
      .then((res) => {
        if (cancelled) return;
        if (isCard) {
          addToast({
            title: `${contactName || 'Contact'} has been added to your contacts.`,
            data: { status: 'success' },
          });
        }
        dispatch({ type: 'CREATE_OK', contactId: res.id, isNew: res.isNew });
      })
      .catch((error) => {
        if (cancelled) return;
        addToast({
          title: 'Could not save this contact',
          description: getApiErrorMessage(error as TFetchError),
          data: { status: 'error' },
        });
        dispatch({ type: 'CREATE_FAIL' });
      })
      .finally(() => {
        inflight.current = null;
      });
    return () => {
      cancelled = true;
    };
    // data/categoryIds/qr* are fixed at the moment of entering 'creating' — intentionally not in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  useEffect(() => {
    if (state.step !== 'merging' || !state.mergeContactId) return;
    if (inflight.current === 'merging') return;
    inflight.current = 'merging';
    let cancelled = false;
    const contactName = state.data.name?.trim();
    const { mergeContactId, categoryIds } = state;

    _mergeBusinessCard({
      ...buildCardMatchPayload(state.data),
      contactId: mergeContactId,
      organizationId: state.data.organizationId ?? null,
    })
      .then(async (result) => {
        if (cancelled) return;
        if (!result?.success) {
          throw new Error('Merge was not acknowledged');
        }
        await addCategoriesToContact(mergeContactId, categoryIds).catch(() => undefined);
        if (cancelled) return;
        addToast({
          title: `${contactName || 'This contact'} is already in your address book.`,
          description: 'New information has been merged.',
          data: { icon: <MergeIcon width={20} height={20} /> },
        });
        dispatch({ type: 'MERGE_OK' });
      })
      .catch(() => {
        if (cancelled) return;
        addToast({
          title: 'Could not merge this contact',
          description: 'Nothing was changed. Check your selection and try again.',
          data: { status: 'error' },
        });
        dispatch({ type: 'MERGE_FAIL' });
      })
      .finally(() => {
        inflight.current = null;
      });

    return () => {
      cancelled = true;
    };
    // data/mergeContactId are fixed on entering 'merging' — intentionally not in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  return { state, dispatch };
}
